// JokerHorizontalList.tsx
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import clsx from "clsx";
import styles from "./JokerHorizontalList.module.css";
import ImageComponent from "./ImageComponent";
import { MOBILE_MAX_WIDTH_PX } from "../util/Constant";
import AssetsPack from "../util/AssetsPack";
/* eslint-disable complexity */

export enum HorizontalScrollGradientColor {
  GRAY,
  BLACK,
}

interface JokerHorizontalListProps<T> {
  items: T[];
  renderItem: (item: T, index: number, isHovered: boolean) => React.ReactNode;
  isBlurEffectEnable?: boolean;
  showScrollbar?: boolean;
  showArrowIndicator?: boolean;
  gradientColor?: HorizontalScrollGradientColor;
  isDotIndicatorConditional?: boolean;
  isDotIndicator?: boolean;
  dotIndicatorClick?: boolean;
}

const JokerHorizontalList = <T,>({
  items,
  renderItem,
  isBlurEffectEnable = false,
  showScrollbar = true,
  showArrowIndicator = false,
  gradientColor = HorizontalScrollGradientColor.BLACK,
  isDotIndicatorConditional = false,
  isDotIndicator = true,
  dotIndicatorClick = false,
}: JokerHorizontalListProps<T>) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<HTMLDivElement[]>([]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [scrollState, setScrollState] = useState({
    atStart: true,
    atEnd: true,
  });
  const [isHovered, setIsHovered] = useState(false);
  const skipScrollCentering = useRef(false);

  const isMobileView = useRef(window.innerWidth <= MOBILE_MAX_WIDTH_PX);
  const isMobile = useRef(
    /Android|webOS|iPhone|iPad/i.test(navigator.userAgent)
  );
  const scrollSpeed = isMobile.current ? 3.5 : 2;

  const updateDynamicPadding = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const gridMax = getComputedStyle(document.documentElement)
      .getPropertyValue("--max-width-inner-grid")
      .trim();

    const base = gridMax.endsWith("rem")
      ? parseFloat(gridMax) *
        parseFloat(getComputedStyle(document.documentElement).fontSize)
      : parseFloat(gridMax);

    const space = Math.max(container.offsetWidth - base, 0);
    document.documentElement.style.setProperty(
      "--dynamic-side-padding",
      `${space / 2 - 16}px`
    );
  }, []);

  useLayoutEffect(() => {
    updateDynamicPadding();
  }, [updateDynamicPadding]);

  useEffect(() => {
    const observer = new ResizeObserver(updateDynamicPadding);
    if (scrollRef.current) observer.observe(scrollRef.current);
    return () => observer.disconnect();
  }, [items]);

  const centerOnScroll = () => {
    if (!scrollRef.current || skipScrollCentering.current) return;
    const center =
      scrollRef.current.scrollLeft + scrollRef.current.offsetWidth / 2;

    let closestIndex = 0;
    let closestDist = Infinity;
    itemRefs.current.forEach((el, i) => {
      const elCenter = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(center - elCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    if (isMobileView.current || activeIndex != null)
      setActiveIndex(closestIndex);
  };

  const onScroll = useCallback(() => {
    const container = scrollRef.current;
    const items = itemRefs.current;
    if (!container || items.length === 0) return;

    const scrollLeft = container.scrollLeft;
    const width = container.offsetWidth;

    const contentWidth = container.scrollWidth;
    const isScrollable = contentWidth > width;

    if (!isScrollable) {
      setScrollState({
        atStart: true,
        atEnd: true,
      });
      return;
    }

    const firstItem = items[0];
    const atStart = scrollLeft <= firstItem.offsetLeft + 2;

    const lastItem = items[items.length - 1];
    const lastItemRightEdge = lastItem.offsetLeft + lastItem.offsetWidth;
    const atEnd = scrollLeft + width >= lastItemRightEdge - 2;

    console.log(atEnd);
    setScrollState({
      atStart: atStart,
      atEnd: atEnd,
    });

    centerOnScroll();
  }, []);

  useLayoutEffect(() => {
    onScroll();
  }, [onScroll]);

  const handleDragScroll = useCallback(() => {
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    const container = scrollRef.current;
    if (!container) return;

    const down = (e: MouseEvent | TouchEvent) => {
      isDragging = true;
      startX = "touches" in e ? e.touches[0].pageX : e.pageX;
      scrollLeft = container.scrollLeft;
    };

    const move = (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      const x = "touches" in e ? e.touches[0].pageX : e.pageX;
      container.scrollLeft = scrollLeft - (x - startX) * scrollSpeed;
    };

    const end = () => {
      isDragging = false;
    };

    container.addEventListener("mousedown", down);
    container.addEventListener("touchstart", down);
    container.addEventListener("mousemove", move);
    container.addEventListener("touchmove", move);
    container.addEventListener("mouseup", end);
    container.addEventListener("touchend", end);
    container.addEventListener("mouseleave", end);

    return () => {
      container.removeEventListener("mousedown", down);
      container.removeEventListener("mousemove", move);
      container.removeEventListener("mouseup", end);
      container.removeEventListener("touchstart", down);
      container.removeEventListener("touchmove", move);
      container.removeEventListener("touchend", end);
      container.removeEventListener("mouseleave", end);
    };
  }, [scrollSpeed]);

  useEffect(() => {
    handleDragScroll();
    const container = scrollRef.current;
    if (container) container.addEventListener("scroll", onScroll);

    return () => {
      if (container) container.removeEventListener("scroll", onScroll);
    };
  }, [onScroll, handleDragScroll]);

  const scrollByAmount = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const items = itemRefs.current;

      if (items.length === 0) return;

      const scrollLeft = container.scrollLeft;
      const containerWidth = container.offsetWidth;

      let targetIndex = 0;
      let closestDistance = Infinity;

      items.forEach((item, index) => {
        const itemLeft = item.offsetLeft;
        const distance = Math.abs(itemLeft - scrollLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          targetIndex = index;
        }
      });

      if (direction === "right" && targetIndex < items.length - 1) {
        targetIndex += 1;
      } else if (direction === "left" && targetIndex > 0) {
        targetIndex -= 1;
      }

      const targetScrollLeft =
        items[targetIndex].offsetLeft -
        containerWidth / 2 +
        items[targetIndex].offsetWidth / 2;

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const handleClick = (index: number) => {
    skipScrollCentering.current = true;
    setActiveIndex(index);
    setTimeout(() => {
      skipScrollCentering.current = false;
    }, 300);
  };

  const handleDotClick = (index: number) => {
    skipScrollCentering.current = true;
    const container = scrollRef.current;
    const item = itemRefs.current[index];

    if (!container || !item) return;

    const containerWidth = container.offsetWidth;

    const targetScrollLeft =
      item.offsetLeft - containerWidth / 2 + item.offsetWidth / 2;
    container.scrollTo({
      left: targetScrollLeft,
      behavior: "smooth",
    });
    setActiveIndex(index);
    setTimeout(() => {
      skipScrollCentering.current = false;
    }, 1000);
  };

  const gradientStyle =
    gradientColor === HorizontalScrollGradientColor.BLACK
      ? styles.black
      : styles.gray;

  return (
    <div className={styles["scroll-wrapper-main"]}>
      <div className={styles["scroll-wrapper"]}>
        <div
          className={clsx(styles["scroll-container"], {
            [styles["hide-scrollbar"]]: !showScrollbar || !isHovered,
          })}
          ref={scrollRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {items.map((item, i) => (
            <div
              key={i}
              ref={(el) => {
                itemRefs.current[i] = el!;
              }}
              onClick={() => handleClick(i)}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex(null)}
              className={clsx(styles["card-wrapper"], {
                [styles["blurred"]]:
                  hoverIndex !== null && hoverIndex !== i && isBlurEffectEnable,
                [styles["first-item"]]: i === 0,
                [styles["last-item"]]: i === items.length - 1,
              })}
            >
              {renderItem(
                item,
                i,
                isMobileView.current
                  ? i === activeIndex
                  : i === (hoverIndex ?? activeIndex)
              )}
            </div>
          ))}
        </div>

        {!scrollState.atStart && (
          <div className={clsx(styles["gradient-left"], gradientStyle)} />
        )}
        {!scrollState.atEnd && (
          <div className={clsx(styles["gradient-right"], gradientStyle)} />
        )}
      </div>

      {isDotIndicator &&
        (isDotIndicatorConditional ? isMobileView.current : true) && (
          <div className={styles["dot-indicator-container"]}>
            {items.map((_, i) => (
              <div
                key={i}
                onClick={() => dotIndicatorClick && handleDotClick(i)}
                className={clsx(styles["dot"], {
                  [styles["active-dot"]]: isMobileView.current
                    ? i === activeIndex
                    : i === (hoverIndex ?? activeIndex),
                })}
              />
            ))}
          </div>
        )}

      {showArrowIndicator && (
        <div className={styles["arrow-container"]}>
          <button
            className={clsx(styles["arrow-button"], {
              [styles["arrow-disabled"]]: scrollState.atStart,
            })}
            disabled={scrollState.atStart}
            onClick={() => scrollByAmount("left")}
          >
            <ImageComponent src={AssetsPack.icons.LEFT_ARROW.default} />
          </button>
          <button
            className={clsx(styles["arrow-button"], {
              [styles["arrow-disabled"]]: scrollState.atEnd,
            })}
            disabled={scrollState.atEnd}
            onClick={() => scrollByAmount("right")}
          >
            <ImageComponent src={AssetsPack.icons.RIGHT_ARROW.default} />
          </button>
        </div>
      )}
    </div>
  );
};

export default JokerHorizontalList;
